<template>
  <div class="card-component">
    <div class="row">
      <div class="col-lg-4 order-lg-2" v-if="sideLayout">
        <div class="list-group mb-2" v-if="cards.links.length > 0">
          <template v-for="(link, index) in cards.links">
            <router-link :to="{ path: link.path }" class="list-group-item d-flex align-items-center" :key="index">
              <span class="fa mr-2" :class="link.icon"></span>
              <span class="align-left">{{ link.title }}</span>
              <span class="badge badge-pill ml-auto" :class="link.badgeClass" v-if="link.badgeClass">
                {{ link.badge }}
              </span>
            </router-link>
          </template>
        </div>
        <transition name="fly">
          <div v-if="$route.meta.showSearch"
            @click="toggleSearch()" v-show="searchHidden">
            <p class="form-control text-muted fake-input">
              Search...
              <span class="pt-1 fa fa-search float-right"></span>
            </p>
          </div>
        </transition>
        <transition name="fly">
          <div v-show="!searchHidden">
            <vue-form ref="form"
              @submit.prevent="search()"
              :sections="cardsSearchForm"
              :is-horizontal="false"
              :options="{
                submit: 'Search',
                submitting: 'Searching...',
                permalink: $route.meta.vueCards,
                method: 'search'
              }">
              <div class="card-header" slot="title">
                Search {{ cards.searchForm.plural }}
                <span class="fa fa-times float-right pt-1 fa-lg text-danger interactive-cursor" @click="toggleSearch()"></span>
              </div>
              <div class="col" slot="actions">
                <button class="btn btn-primary" type="submit">Search</button>
              </div>
            </vue-form>
          </div>
        </transition>
      </div>
      <div :class="{
        'col-lg-8 order-lg-1': sideLayout,
        'col': !sideLayout
      }">
        <organization-picker
          v-if="cards.organizations.length > 1"
          :organizations="cards.organizations"
          v-model="selectedOrganization">
        </organization-picker>
        <div class="toolbar text-right">
          <router-link class="btn btn-primary" v-if="$route.meta.allowNew && (!$route.meta.newDisabled || isFinanceAdmin)"
          :to="{ path: 'new', append: true }">
            <span class="fa fa-plus"></span> New
          </router-link>
          <button v-if="$route.meta.allowNew && $route.meta.newDisabled && !isFinanceAdmin" class="btn btn-primary" disabled>
              <span class="fa fa-plus"></span> New
          </button>
          <button type="button" class="btn btn-primary"
            @click="exportCsv()" v-if="$route.meta.allowExport">
            <span class="fa fa-download"></span> Export
          </button>
          <button v-if="$route.meta.allowToggle" class="btn btn-primary" @click="tableDisplay = !tableDisplay">
            <span class="fa fa-fw" :class="!tableDisplay ? 'fa-bars' : 'fa-table'"></span>
            Toggle Display
          </button>
          <form action="/api/admin/export" method="post" ref="exportForm">
            <input type="hidden" name="exportObjects" v-model="$route.meta.vueCards"/>
            <input type="hidden" name="searchForm" v-model="formData"/>
            <input type="hidden" name="selectedOrganization" v-model="selectedOrganizationFormData"/>
          </form>
        </div>
        <paging-input ref="paging"
          @change="changePage"
          :options="{
            current: (cards.offset + cards.limit) / cards.limit,
            totalResults: cards.count,
            limit: cards.limit
          }"
          v-if="cards.count">
        </paging-input>
        <div class="card card-primary mb-3">
          <div class="card-header">
            <span class="mr-1 fa" :class="$route.meta.icon"></span>
            {{ $route.meta.vueTitle }}
            <span class="badge badge-primary" v-if="cards.count">
              {{ cards.count }} Result<span v-if="cards.count !== 1">s</span>
            </span>
          </div>
          <div class="card-body p-1 pb-0">
            <template v-if="cards.rows.length > 0">
              <template v-if="tableDisplay">
                <responsive-table @orderColumn="sort($event)" :hideTitle="true" :table="{ columns: cards.columns }" :default-sort-column="sortColumn" :default-sort="sortDirection">
                  <template slot="rows">
                    <component v-for="(row, i) in cards.rows" :is="parseType($route.meta.vueCards) + '-row'"
                      :object="row"
                      :key="i">
                    </component>
                  </template>
                </responsive-table>
              </template>
              <template v-else-if="cards.permalink === $route.meta.vueCards">
                <div class="card border-dark mb-2"
                v-for="(object, i) in cards.rows"
                :class="{ 'bg-light': i % 2 == 0 }"
                :key="i">
                  <component :is="parseType($route.meta.vueCards)" :object="object">
                  </component>
                </div>
              </template>
            </template>
            <p class="alert alert-danger text-strong" v-if="!cards.rows.length">
              <strong>{{ cards.searchForm.noResults }}</strong>
            </p>
          </div>
        </div>
        <paging-input ref="paging"
          @change="changePage"
          :options="{
            current: (cards.offset + cards.limit) / cards.limit,
            totalResults: cards.count,
            limit: cards.limit
          }"
          v-if="cards.count">
        </paging-input>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { LOAD_CARDS, OPEN_ERROR_MODAL } from '@/vuex/types'
import Account from '@/components/cards/Account'
import AccountMessage from '@/components/cards/AccountMessage'
import AccountNote from '@/components/cards/AccountNote'
import AccountRow from '@/components/rows/Account'
import ApplicationRow from '@/components/rows/Application'
import AccountType from '@/components/cards/AccountType'
import AccountTypeRow from '@/components/rows/AccountType'
import AdminRequest from '@/components/cards/AdminRequest'
import Application from '@/components/cards/Application'
import Car from '@/components/cards/Car'
import CarRow from '@/components/rows/Car'
import CarClass from '@/components/cards/CarClass'
import CarClassRow from '@/components/rows/CarClass'
import CarDamage from '@/components/cards/CarDamage'
import CarFeature from '@/components/cards/CarFeature'
import CarFeatureRow from '@/components/rows/CarFeature'
import CarHardware from '@/components/cards/CarHardware'
import CarHardwareRow from '@/components/rows/CarHardware'
import CarRestriction from '@/components/cards/CarRestriction'
import CarRestrictionRow from '@/components/rows/CarRestriction'
import CarType from '@/components/cards/CarType'
import CarTypeRow from '@/components/rows/CarType'
import Contract from '@/components/cards/Contract'
import DashboardMessage from '@/components/cards/DashboardMessage'
import DemoAccountRow from '@/components/rows/DemoAccount'
import DemoAccount from '@/components/cards/DemoAccount'
import DirectDebit from '@/components/cards/DirectDebit'
import DirectDebitRow from '@/components/rows/DirectDebit'
import EmailTemplate from '@/components/cards/EmailTemplate'
import EmailTemplateRow from '@/components/rows/EmailTemplate'
import ExtraCost from '@/components/cards/ExtraCost'
import ExtraCostRow from '@/components/rows/ExtraCost'
import FinanceCode from '@/components/cards/FinanceCode'
import FinanceCodeRow from '@/components/rows/FinanceCode'
import Lot from '@/components/cards/Lot'
import LotRelocations from '@/components/cards/LotRelocations'
import LotRow from '@/components/rows/Lot'
import NegativeAccountRow from '@/components/rows/NegativeAccount'
import NegativeAccount from '@/components/cards/NegativeAccount'
import NPortRow from '@/components/rows/NPort'
import NPort from '@/components/cards/NPort'
import Organization from '@/components/cards/Organization'
import OrgLogo from '@/components/OrgLogo'
import OrganizationPicker from '@/components/inputs/OrganizationPicker'
import PagingInput from '@/components/inputs/PagingInput'
import PetrolCardRow from '@/components/rows/PetrolCard'
import PromoCode from '@/components/cards/PromoCode'
import TrailingRebatePromo from '@/components/cards/TrailingRebatePromo'
import Rate from '@/components/cards/Rate'
import RateRow from '@/components/rows/Rate'
import Reservation from '@/components/cards/Reservation'
import ReservationPromo from '@/components/cards/ReservationPromo'
import ReservationRow from '@/components/rows/Reservation'
import ResponsiveTable from '@/components/ResponsiveTable'
import SimCard from '@/components/cards/SimCard'
import SimCardRow from '@/components/rows/SimCard'
import Smartcard from '@/components/cards/Smartcard'
import SmartcardRow from '@/components/rows/Smartcard'
import Ticket from '@/components/cards/Ticket'
import Valet from '@/components/cards/Valet'
import ValetRow from '@/components/rows/Valet'
import VueForm from '@/components/Form'

export default {
  name: 'Cards',
  components: {
    Account,
    AccountMessage,
    AccountNote,
    AccountRow,
    AccountType,
    AccountTypeRow,
    AdminRequest,
    Application,
    ApplicationRow,
    Car,
    CarRow,
    CarClass,
    CarClassRow,
    CarDamage,
    CarFeature,
    CarFeatureRow,
    CarHardware,
    CarHardwareRow,
    CarRestriction,
    CarRestrictionRow,
    CarType,
    CarTypeRow,
    Contract,
    DashboardMessage,
    DemoAccount,
    DemoAccountRow,
    DirectDebit,
    DirectDebitRow,
    EmailTemplate,
    EmailTemplateRow,
    ExtraCost,
    ExtraCostRow,
    FinanceCode,
    FinanceCodeRow,
    Lot,
    LotRelocations,
    LotRow,
    NPortRow,
    NPort,
    NegativeAccount,
    NegativeAccountRow,
    Organization,
    OrgLogo,
    OrganizationPicker,
    PagingInput,
    PetrolCardRow,
    PromoCode,
    Rate,
    RateRow,
    Reservation,
    ReservationPromo,
    TrailingRebatePromo,
    ReservationRow,
    ResponsiveTable,
    SimCard,
    SimCardRow,
    Smartcard,
    SmartcardRow,
    Ticket,
    Valet,
    ValetRow,
    VueForm
  },
  data () {
    return {
      tableDisplay: false,
      searchForm: [],
      searchOffset: 0,
      searchLimit: 10,
      searchHidden: true,
      sortColumn: false,
      sortDirection: false,
      newSearch: true,
      organizations: [],
      selectedOrganization: null,
      noResults: 'Your search returned 0 results',
      pagingOptions: {
        totalResults: 10,
        limit: 10,
        current: 1
      },
      links: [],
      exportObjects: '',
      financeAdmin: false,
      allowNew: false,
      formData: '',
      selectedOrganizationFormData: ''
    }
  },
  computed: {
    imageURL () {
      return 'https://techcar-dev.s3.amazonaws.com/'
    },
    sideLayout () {
      return this.$route.meta.showSearch || this.cards.links.length > 0
    },
    ...mapGetters([
      'cards',
      'loggedInUser',
      'cardsSearchForm',
      'isFinanceAdmin'
    ])
  },
  mounted () {
    this.tableDisplay = this.$route.meta.tableDisplay || false
    this.createNewSearch()
  },
  watch: {
    $route () {
      this.tableDisplay = this.$route.meta.tableDisplay || false
      this.createNewSearch()
    },
    async selectedOrganization () {
      await this.$nextTick()
      this.createNewSearch()
    }
  },
  methods: {
    sort (column) {
      this.sortColumn = column.name
      this.sortDirection = column.sortDirection
      this.search()
    },
    async exportCsv () {
      let searchForm = Array.from(this.cardsSearchForm)
      searchForm.splice(-1, 1)
      this.formData = JSON.stringify(searchForm)
      this.selectedOrganizationFormData = JSON.stringify(this.selectedOrganization)
      await this.$nextTick()
      this.$refs.exportForm.submit()
    },
    async createNewSearch () {
      this.searchOffset = 0
      this.hasMore = true
      try {
        await this.$store.dispatch(LOAD_CARDS, {
          cards: this.$route.meta.vueCards,
          searchForm: [],
          sortDirection: this.sortDirection,
          sortColumn: this.sortColumn,
          offset: this.searchOffset,
          limit: this.cards.limit,
          newSearch: true,
          selectedOrganization: this.selectedOrganization
        })
      } catch (err) {
        this.$store.dispatch(OPEN_ERROR_MODAL, err)
      }
    },
    async search () {
      await this.$refs.form.validateForm()
      if (this.$refs.form.validForm) {
        let searchForm = Array.from(this.cardsSearchForm)
        let limit = searchForm.splice(-1, 1).pop()
        try {
          await this.$store.dispatch(LOAD_CARDS, {
            cards: this.$route.meta.vueCards,
            searchForm,
            sortDirection: this.sortDirection,
            sortColumn: this.sortColumn,
            offset: this.searchOffset,
            limit: limit.value,
            newSearch: this.newSearch,
            selectedOrganization: this.selectedOrganization
          })
        } catch (err) {
          this.$store.dispatch(OPEN_ERROR_MODAL, err)
        }
      }
    },
    async toggleSearch () {
      this.searchHidden = !this.searchHidden
      if (!this.searchHidden) {
        this.$refs.form.$el.querySelectorAll('input[type=text]')[0].focus()
      }
    },
    async changePage (page) {
      this.searchOffset = (page - 1) * this.cards.limit
      await this.search()
    },
    parseType (type) {
      switch (type) {
        case 'financeCodes':
          return 'finance-code'
        case 'notes':
          return 'account-note'
        case 'applications':
        case 'declinedApplications':
        case 'incompleteApplications':
        case 'deletedApplications':
          return 'application'
        case 'demoAccounts':
          return 'demo-account'
        case 'negativeMembers':
          return 'negative-account'
        case 'accounts':
        case 'activeMembers':
        case 'inactiveMembers':
        case 'restrictedMembers':
        case 'quitMembers':
        case 'blacklistedMembers':
          return 'account'
        case 'reservations':
        case 'maintenance':
          return 'reservation'
        case 'activeCars':
        case 'soldCars':
        case 'retiredCars':
        case 'unusedCars':
          return 'car'
        case 'accountTypes':
          return 'account-type'
        case 'smartcards':
          return 'smartcard'
        case 'rates':
          return 'rate'
        case 'valets':
          return 'valet'
        case 'carClasses':
          return 'car-class'
        case 'carTypes':
          return 'car-type'
        case 'boxes':
          return 'car-hardware'
        case 'nPorts':
          return 'n-port'
        case 'simcards':
          return 'sim-card'
        case 'petrolCards':
          return 'petrol-card'
        case 'carRestrictions':
          return 'car-restriction'
        case 'lots':
          return 'lot'
        case 'lotRelocations':
          return 'lot-relocations'
        case 'costs':
          return 'extra-cost'
        case 'emailTemplates':
          return 'email-template'
        case 'organizations':
          return 'organization'
        case 'accountMessages':
          return 'account-message'
        case 'dashboardMessages':
          return 'dashboard-message'
        case 'carFeatures':
          return 'car-feature'
        case 'promos':
          return 'promo-code'
        case 'reservationPromos':
          return 'reservation-promo'
        case 'trailingRebatePromos':
          return 'trailing-rebate-promo'
        case 'tickets':
        case 'completedTickets':
          return 'ticket'
        case 'problems':
        case 'resolvedProblems':
          return 'admin-request'
        case 'contracts':
          return 'contract'
        case 'repairs':
          return 'car-damage'
        case 'directDebits':
          return 'direct-debit'
      }
    },
    getOrganization (object) {
      if (this.$route.meta.vueCards === 'organizations') {
        return object
      } else if (object.Organization) {
        return object.Organization
      } else if (object.Account && object.Account.Organization) {
        return object.Account.Organization
      } else if (object.AccountType && object.AccountType.Organization) {
        return object.AccountType.Organization
      } else if (object.AccountTypes && object.AccountTypes[0] && object.AccountTypes[0].Organization) {
        return object.AccountTypes[0].Organization
      } else {
        return false
      }
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/Cards.vue